import { Paper, Stack, Typography, useTheme } from '@mui/material';
import type { GetDailyEventsDigestedResponse } from '@weft/api-definition';
import type { ItemOf } from '@weft/utils';
import { buildPeriod, getPeriodDaysWithUtc, sumBy } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import { ColorDot } from 'apps/app/src/components/design/ColorDot';
import { withSuspenseBoundary } from 'apps/app/src/components/utility/SuspenseBoundary';
import { useGetDailyEventsDigested } from 'apps/app/src/hooks/requests/use-get-daily-events-digested';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { DataDashboardLoadingSkeleton } from '../DataDashboardLoadingSkeleton';
import { EventsDigestedDisplayIntervalSelector } from './events-digested-display-interval-selector';
import type { EventsDigestedLineChartDisplayInterval } from './events-digested-line-chart/events-digested-line-chart';
import { DISPLAY_INTERVALS, EventsDigestedLineChart } from './events-digested-line-chart/events-digested-line-chart';
import { getEventsIntervalLabel } from './events-digested-section-utils/get-events-interval-label';

const EVENTS_PER_MONTH = 1;

const EventsDigestedSection: React.FC = () => {
  const vyTheme = useTheme();
  const { data: { dailyEventsDigested: dailyEventWithMissingDates } } = useGetDailyEventsDigested();
  const [selectedDisplayInterval, setSelectedDisplayInterval] = useState<EventsDigestedLineChartDisplayInterval>(DISPLAY_INTERVALS.monthly);
  const dailyEventsDigested = useMemo(() => getDailyEventsWithMissingDates(dailyEventWithMissingDates), [dailyEventWithMissingDates]);
  const thisMonthDailyEvents = useMemo(() => filterThisMonthEvents(dailyEventsDigested), [dailyEventsDigested]);

  const eventsToDisplay = selectedDisplayInterval === 'daily' ? thisMonthDailyEvents : dailyEventsDigested;

  return (
    <Paper
      variant='outlined' 
      sx={(theme) => ({
        boxShadow: theme.shadows[1],
        padding: 3,
      })}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        paddingBottom={2}
      >
        <Typography variant='h6'>Metered Events</Typography>
        <EventsDigestedDisplayIntervalSelector
          defaultDisplayInterval={selectedDisplayInterval}
          onSelectDisplayInterval={(displayInterval) => setSelectedDisplayInterval(displayInterval)}
        />
      </Stack>
      <Stack
        justifyContent="space-between"
        alignItems="center"
        direction="row"
      >
        <Stack
          gap={0.5}
          direction="column"
        >
          <Typography
            color={vyTheme.palette.text.secondary}
            variant='body2'
          >
            {`Digested ${getEventsIntervalLabel(selectedDisplayInterval)}`}
          </Typography>
          <Typography variant='h5'>{formatNumber(sumBy(eventsToDisplay, 'count'))}</Typography>
        </Stack>
        <Stack
          gap={1}
          alignItems="center"
          direction="row"
        >
          <ColorDot color={vyTheme.palette.secondary.main}/>
          <Typography
            color={vyTheme.palette.text.secondary}
            variant='body2'
          >
            Events Digested
          </Typography>
        </Stack>
      </Stack>
      <EventsDigestedLineChart
        displayInterval={selectedDisplayInterval}
        dailyEventsDigested={eventsToDisplay}
        eventsPerMonth={EVENTS_PER_MONTH}
      />
    </Paper>
  );
};

function filterThisMonthEvents(dailyEvents: GetDailyEventsDigestedResponse['dailyEventsDigested']) {
  const startOfMonth = dayjs().startOf('month');

  return dailyEvents.filter(({ date }) => dayjs(date).isAfter(startOfMonth));
}

function getDailyEventsWithMissingDates(dailyEvents: GetDailyEventsDigestedResponse['dailyEventsDigested']) {
  const yesterday = dayjs().subtract(1, 'day').startOf('day');
  const lastYear = yesterday.subtract(1, 'year');
  const lastYearToToday = buildPeriod(lastYear.toDate(), yesterday.toDate());

  const periodDays = getPeriodDaysWithUtc(lastYearToToday);

  return periodDays.map<ItemOf<GetDailyEventsDigestedResponse['dailyEventsDigested']>>((date) => {
    const dailyEvent = dailyEvents.find(({ date: eventDate }) => dayjs(eventDate).isSame(date, 'day'));

    return dailyEvent ?? { date: date.toDate(), count: 0 };
  });
}

const EventsDigestedSectionWithSuspense = withSuspenseBoundary(EventsDigestedSection, { suspenseProps: { fallback: <DataDashboardLoadingSkeleton/> } });

export { EventsDigestedSectionWithSuspense as EventsDigestedSection };